// Company stores (S3, 2026-10-12) — /store-signoff/:token, public.
//
// The client's one link from their rep: the store name, the logo we'll use
// (approving it IS the logo proof), the products, how people pay, then
// "Approve store" or "Request changes". The link is the credential; no
// sign-in. API: GET/POST /portal/store-signoff/:token[/approve|/request-changes].
// Once approved the link answers only { status: 'approved', storeName }.
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiError } from "@/lib/api";
import {
  apiMessage,
  approveSignoff,
  fetchSignoff,
  isApprovedStub,
  paymentOptionText,
  requestSignoffChanges,
  type SignoffView,
} from "@/lib/portal-store";
import { StoreLoading, StoreNotice, StorePublicShell } from "@/components/StorePublicShell";
import { StoreProductGrid } from "@/components/StoreProductCard";

const SIGN_IN_STATE = { from: "/store" };

export function StoreSignoffPage() {
  const { token = "" } = useParams<{ token: string }>();
  const [done, setDone] = useState<"approved" | "already" | "changes" | null>(null);

  const q = useQuery({
    queryKey: ["store-signoff", token],
    queryFn: () => fetchSignoff(token),
    retry: false,
    staleTime: 0,
  });

  if (done === "approved") {
    return (
      <StorePublicShell narrow>
        <StoreNotice title="Your store is live">
          <p>Your store is live. Sign in to open it.</p>
          <p>New here? Check your email (and spam) for a link to set your password.</p>
          <p>
            <Link
              to="/login"
              state={SIGN_IN_STATE}
              className="inline-block rounded-md bg-ink px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-ink-700"
            >
              Sign in
            </Link>
          </p>
        </StoreNotice>
      </StorePublicShell>
    );
  }
  if (done === "changes") {
    return (
      <StorePublicShell narrow>
        <StoreNotice title="Thanks, we've got it">
          <p>Your rep has your notes and will update the store. We'll send you a new link to look at it again.</p>
        </StoreNotice>
      </StorePublicShell>
    );
  }

  if (q.isPending) {
    return (
      <StorePublicShell>
        <StoreLoading />
      </StorePublicShell>
    );
  }

  if (q.isError) {
    const notFound = q.error instanceof ApiError && q.error.status === 404;
    return (
      <StorePublicShell narrow>
        <StoreNotice title={notFound ? "This link isn't valid" : "We couldn't open this store"}>
          <p>
            {notFound
              ? "It may have been replaced by a newer link. Please ask your rep at other to send it again."
              : apiMessage(q.error)}
          </p>
        </StoreNotice>
      </StorePublicShell>
    );
  }

  const data = q.data;
  if (done === "already" || isApprovedStub(data) || data.state === "approved") {
    return (
      <StorePublicShell narrow>
        <StoreNotice title="This store is already approved.">
          <p>{isApprovedStub(data) ? data.storeName : data.store.name} is live.</p>
          <p>
            <Link to="/login" state={SIGN_IN_STATE} className="font-semibold text-ink underline-offset-2 hover:underline">
              Sign in to your store
            </Link>
          </p>
        </StoreNotice>
      </StorePublicShell>
    );
  }

  if (data.state === "changes_requested") {
    return (
      <StorePublicShell narrow>
        <StoreNotice title="Your changes are with your rep">
          <p>They'll update {data.store.name} and send you a new link to approve.</p>
          {data.note && (
            <p className="whitespace-pre-line rounded-md border border-rule bg-canvas px-4 py-3 text-left text-brown-500">{data.note}</p>
          )}
        </StoreNotice>
      </StorePublicShell>
    );
  }

  return <SignoffPreview token={token} view={data} onDone={setDone} />;
}

function SignoffPreview({
  token,
  view,
  onDone,
}: {
  token: string;
  view: SignoffView;
  onDone: (d: "approved" | "already" | "changes") => void;
}) {
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState("");

  const approve = useMutation({
    mutationFn: () => approveSignoff(token),
    onSuccess: (r) => onDone(r.status === "already_approved" ? "already" : "approved"),
  });
  const changes = useMutation({
    mutationFn: () => requestSignoffChanges(token, note.trim()),
    onSuccess: () => onDone("changes"),
  });
  const busy = approve.isPending || changes.isPending;
  const payments = view.store.paymentOptions ?? [];

  return (
    <StorePublicShell>
      <p className="mb-2 text-sm text-brown-400">
        {view.contactFirstName ? `Hi ${view.contactFirstName}, here's` : "Here's"} the store we've built for {view.store.companyName}.
      </p>
      <h1 className="mb-8 font-display text-3xl font-bold tracking-tight text-ink">{view.store.name}</h1>

      <section className="mb-8 rounded-lg border border-rule bg-white p-8 shadow-sm">
        <h2 className="mb-1 font-display text-xl font-bold tracking-tight">Your logo</h2>
        <p className="mb-6 text-sm text-brown-400">This is the logo we'll use on everything in your store.</p>
        <div className="flex min-h-[220px] items-center justify-center rounded-md border border-rule bg-canvas p-8">
          {view.store.logo?.url ? (
            <img src={view.store.logo.url} alt={`${view.store.name} logo`} className="max-h-[280px] max-w-full object-contain" />
          ) : (
            <p className="text-sm text-brown-400">Your rep hasn't added the logo yet. Please ask them before approving.</p>
          )}
        </div>
      </section>

      <section className="mb-8">
        <h2 className="mb-1 font-display text-xl font-bold tracking-tight">What's in the store</h2>
        <p className="mb-4 text-sm text-brown-400">
          {view.products.length === 1 ? "1 product" : `${view.products.length} products`}, each with your logo.
        </p>
        {view.products.length > 0 ? (
          <StoreProductGrid products={view.products} />
        ) : (
          <p className="rounded-lg border border-rule bg-white p-6 text-sm text-brown-400">No products yet. Ask your rep to add some.</p>
        )}
      </section>

      {payments.length > 0 && (
        <section className="mb-8 rounded-lg border border-rule bg-white p-8 shadow-sm">
          <h2 className="mb-3 font-display text-xl font-bold tracking-tight">How people pay</h2>
          <ul className="list-disc space-y-1.5 pl-5 text-sm text-brown-500">
            {payments.map((p) => (
              <li key={p}>{paymentOptionText(p, view.store.invoiceTerms)}</li>
            ))}
          </ul>
        </section>
      )}

      <section className="rounded-lg border border-rule bg-white p-8 shadow-sm">
        <h2 className="mb-1 font-display text-xl font-bold tracking-tight">Ready to go?</h2>
        <p className="mb-6 text-sm text-brown-400">
          Approving opens the store and makes you its admin. You can then add your people and choose which orders need your OK.
          By approving, you confirm this logo is correct to print.
        </p>

        {(approve.isError || changes.isError) && (
          <div role="alert" className="mb-4 rounded-md border border-rust-200 bg-rust-50 px-4 py-3 text-sm text-rust-500">
            {apiMessage(approve.error ?? changes.error)}
          </div>
        )}

        {!asking ? (
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              disabled={busy}
              onClick={() => approve.mutate()}
              className="rounded-md bg-ink px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-ink-700 disabled:opacity-50"
            >
              {approve.isPending ? "Approving…" : "Approve store"}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setAsking(true)}
              className="rounded-md border border-rule bg-white px-5 py-3 text-sm font-semibold text-ink transition-colors hover:bg-canvas disabled:opacity-50"
            >
              Request changes
            </button>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (note.trim()) changes.mutate();
            }}
            className="space-y-3"
          >
            <label htmlFor="signoff-note" className="block text-sm font-semibold text-brown-500">
              What should we change?
            </label>
            <textarea
              id="signoff-note"
              rows={5}
              maxLength={4000}
              autoFocus
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="For example: use the white version of the logo, and add a quarter-zip."
              className="w-full rounded-md border border-cream-300 bg-white p-3 text-sm text-brown-500 placeholder:text-[#8a8a85] focus:border-peach-400 focus:outline-none"
            />
            <div className="flex flex-wrap gap-3">
              <button
                type="submit"
                disabled={busy || !note.trim()}
                className="rounded-md bg-ink px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-ink-700 disabled:opacity-50"
              >
                {changes.isPending ? "Sending…" : "Send to my rep"}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => setAsking(false)}
                className="rounded-md border border-rule bg-white px-5 py-3 text-sm font-semibold text-ink transition-colors hover:bg-canvas disabled:opacity-50"
              >
                Back
              </button>
            </div>
          </form>
        )}
      </section>
    </StorePublicShell>
  );
}
